<template>
  <div class="pp-wrap">
    <!-- Шапка профиля -->
    <div class="pp-hero">
      <div class="pp-avatar">
        <span class="pp-avatar-letter">{{ avatarLetter }}</span>
      </div>
      <div class="pp-hero-info">
        <h1 class="pp-name">{{ authStore.user?.name || 'Пользователь' }}</h1>
        <p class="pp-email">{{ authStore.user?.email }}</p>
        <div class="pp-role-badge">{{ authStore.user?.role === 'admin' ? '👑 Администратор' : '👤 Клиент' }}</div>
      </div>
      <div class="pp-hero-actions">
        <router-link to="/orders" class="pp-link-btn">📋 Мои заказы</router-link>
      </div>
    </div>

    <!-- Вкладки -->
    <div class="pp-tabs">
      <button @click="activeTab = 'profile'" class="pp-tab" :class="{ 'pp-tab-active': activeTab === 'profile' }">
        👤 Личные данные
      </button>
      <button @click="activeTab = 'password'" class="pp-tab" :class="{ 'pp-tab-active': activeTab === 'password' }">
        🔐 Безопасность
      </button>
    </div>

    <!-- Вкладка профиля -->
    <div v-if="activeTab === 'profile'" class="pp-section">
      <div v-if="error" class="pp-alert pp-alert-error">⚠️ {{ error }}</div>
      <div v-if="successMessage" class="pp-alert pp-alert-success">✅ {{ successMessage }}</div>

      <form @submit.prevent="updateProfile" class="pp-form">
        <div class="pp-form-row">
          <div class="pp-field">
            <label class="pp-label">Имя</label>
            <input v-model="profileForm.name" type="text" required class="pp-input" placeholder="Иван Иванов" />
          </div>
          <div class="pp-field">
            <label class="pp-label">Email <span class="pp-hint">(нельзя изменить)</span></label>
            <input :value="profileForm.email" type="email" disabled class="pp-input pp-input-disabled" />
          </div>
        </div>

        <div class="pp-form-row">
          <div class="pp-field">
            <label class="pp-label">Телефон</label>
            <input v-model="profileForm.phone" type="tel" class="pp-input" placeholder="+38 (0__) ___-__-__" />
          </div>
          <div class="pp-field">
            <label class="pp-label">Город</label>
            <input v-model="profileForm.city" type="text" class="pp-input" placeholder="Харьков" />
          </div>
        </div>

        <div class="pp-form-row">
          <div class="pp-field pp-field-wide">
            <label class="pp-label">Адрес</label>
            <input v-model="profileForm.address" type="text" class="pp-input" placeholder="ул. Примерная, д. 1, кв. 10" />
          </div>
          <div class="pp-field">
            <label class="pp-label">Почтовый индекс</label>
            <input v-model="profileForm.postalCode" type="text" class="pp-input" placeholder="61000" />
          </div>
        </div>

        <div class="pp-form-footer">
          <div class="pp-save-hint">Данные используются для оформления заказов</div>
          <button type="submit" :disabled="loading" class="pp-btn-save">
            <span v-if="loading" class="pp-spin"></span>
            {{ loading ? 'Сохранение...' : 'Сохранить изменения' }}
          </button>
        </div>
      </form>
    </div>

    <!-- Вкладка безопасности -->
    <div v-if="activeTab === 'password'" class="pp-section">
      <div v-if="passwordError" class="pp-alert pp-alert-error">⚠️ {{ passwordError }}</div>
      <div v-if="passwordSuccess" class="pp-alert pp-alert-success">✅ {{ passwordSuccess }}</div>

      <div class="pp-security-info">
        <div class="pp-security-icon">🔒</div>
        <div>
          <h3>Смена пароля</h3>
          <p>Используйте надёжный пароль — минимум 6 символов, лучше с цифрами и буквами.</p>
        </div>
      </div>

      <form @submit.prevent="changePassword" class="pp-form">
        <div class="pp-field">
          <label class="pp-label">Текущий пароль</label>
          <input v-model="passwordForm.oldPassword" type="password" required class="pp-input" placeholder="Введите текущий пароль" />
        </div>
        <div class="pp-form-row">
          <div class="pp-field">
            <label class="pp-label">Новый пароль</label>
            <input v-model="passwordForm.newPassword" type="password" required minlength="6" class="pp-input" placeholder="Минимум 6 символов" />
          </div>
          <div class="pp-field">
            <label class="pp-label">Повторите новый пароль</label>
            <input v-model="passwordForm.confirmPassword" type="password" required minlength="6" class="pp-input" placeholder="Ещё раз" />
          </div>
        </div>
        <div class="pp-form-footer">
          <div></div>
          <button type="submit" :disabled="passwordLoading" class="pp-btn-save">
            <span v-if="passwordLoading" class="pp-spin"></span>
            {{ passwordLoading ? 'Обработка...' : 'Изменить пароль' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useAuthStore } from '../stores/auth';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

export default {
  name: 'ProfilePage',
  setup() {
    const authStore = useAuthStore();
    const activeTab = ref('profile');
    const loading = ref(false);
    const passwordLoading = ref(false);
    const error = ref(null);
    const successMessage = ref(null);
    const passwordError = ref(null);
    const passwordSuccess = ref(null);

    const profileForm = ref({ name: '', email: '', phone: '', city: '', address: '', postalCode: '' });
    const passwordForm = ref({ oldPassword: '', newPassword: '', confirmPassword: '' });

    const avatarLetter = computed(() => (authStore.user?.name || 'U')[0].toUpperCase());

    const loadProfile = () => {
      if (authStore.user) {
        profileForm.value = {
          name: authStore.user.name || '',
          email: authStore.user.email || '',
          phone: authStore.user.phone || '',
          city: authStore.user.city || '',
          address: authStore.user.address || '',
          postalCode: authStore.user.postalCode || ''
        };
      }
    };

    const updateProfile = async () => {
      error.value = null; successMessage.value = null;
      if (!profileForm.value.name.trim()) { error.value = 'Введите имя'; return; }
      loading.value = true;
      try {
        const res = await axios.put(`${API_URL}/users/profile`,
          { name: profileForm.value.name, phone: profileForm.value.phone, city: profileForm.value.city, address: profileForm.value.address, postalCode: profileForm.value.postalCode },
          { headers: { Authorization: `Bearer ${authStore.token}` } }
        );
        authStore.user = res.data.user;
        successMessage.value = 'Профиль успешно обновлён';
        setTimeout(() => { successMessage.value = null; }, 3000);
      } catch (err) { error.value = err.response?.data?.error || 'Ошибка при обновлении'; }
      finally { loading.value = false; }
    };

    const changePassword = async () => {
      passwordError.value = null; passwordSuccess.value = null;
      if (passwordForm.value.newPassword !== passwordForm.value.confirmPassword) { passwordError.value = 'Пароли не совпадают'; return; }
      if (passwordForm.value.newPassword.length < 6) { passwordError.value = 'Пароль должен быть не менее 6 символов'; return; }
      passwordLoading.value = true;
      try {
        await axios.post(`${API_URL}/users/change-password`,
          { oldPassword: passwordForm.value.oldPassword, newPassword: passwordForm.value.newPassword },
          { headers: { Authorization: `Bearer ${authStore.token}` } }
        );
        passwordSuccess.value = 'Пароль успешно изменён';
        passwordForm.value = { oldPassword: '', newPassword: '', confirmPassword: '' };
        setTimeout(() => { passwordSuccess.value = null; }, 3000);
      } catch (err) { passwordError.value = err.response?.data?.error || 'Ошибка при изменении пароля'; }
      finally { passwordLoading.value = false; }
    };

    onMounted(loadProfile);
    return { authStore, activeTab, loading, passwordLoading, error, successMessage, passwordError, passwordSuccess, profileForm, passwordForm, avatarLetter, updateProfile, changePassword };
  }
};
</script>

<style scoped>
* { box-sizing: border-box; }
.pp-wrap { max-width: 760px; margin: 0 auto; }

/* Hero */
.pp-hero { background: linear-gradient(135deg, #26215c 0%, #1a3a2a 100%); border-radius: 20px; padding: 2rem; display: flex; align-items: center; gap: 1.5rem; margin-bottom: 1.5rem; color: #fff; }
.pp-avatar { width: 72px; height: 72px; border-radius: 50%; background: rgba(255,255,255,0.15); display: flex; align-items: center; justify-content: center; flex-shrink: 0; border: 3px solid rgba(255,255,255,0.25); }
.pp-avatar-letter { font-size: 30px; font-weight: 800; color: #fff; }
.pp-hero-info { flex: 1; }
.pp-name { font-size: 22px; font-weight: 800; margin-bottom: 4px; }
.pp-email { font-size: 14px; color: rgba(255,255,255,0.65); margin-bottom: 8px; }
.pp-role-badge { display: inline-block; background: rgba(255,255,255,0.12); font-size: 12px; font-weight: 700; padding: 4px 12px; border-radius: 20px; }
.pp-hero-actions { flex-shrink: 0; }
.pp-link-btn { background: rgba(255,255,255,0.15); color: #fff; padding: 10px 18px; border-radius: 10px; font-size: 13px; font-weight: 700; text-decoration: none; transition: background 0.15s; display: inline-block; }
.pp-link-btn:hover { background: rgba(255,255,255,0.25); }

/* Tabs */
.pp-tabs { display: flex; gap: 4px; background: #fff; border: 1px solid #e0dff8; border-radius: 14px; padding: 6px; margin-bottom: 1.5rem; }
.pp-tab { flex: 1; background: transparent; border: none; padding: 10px; border-radius: 10px; font-size: 14px; font-weight: 600; color: #888; cursor: pointer; transition: all 0.15s; }
.pp-tab:hover { color: #534ab7; background: #f4f3ff; }
.pp-tab-active { background: #534ab7 !important; color: #fff !important; }

/* Section */
.pp-section { background: #fff; border: 1px solid #e0dff8; border-radius: 20px; padding: 2rem; }

/* Alerts */
.pp-alert { border-radius: 10px; padding: 12px 16px; font-size: 14px; margin-bottom: 1.25rem; }
.pp-alert-error { background: #ffeaea; color: #c0392b; border: 1px solid #f5c6c6; }
.pp-alert-success { background: #e1f5ee; color: #1d6648; border: 1px solid #a7e0c8; }

/* Form */
.pp-form { display: flex; flex-direction: column; gap: 1.25rem; }
.pp-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }
.pp-field { display: flex; flex-direction: column; gap: 6px; }
.pp-field-wide { grid-column: span 1; }
.pp-label { font-size: 13px; font-weight: 700; color: #444; }
.pp-hint { font-size: 11px; font-weight: 400; color: #aaa; }
.pp-input { background: #f4f3ff; border: 1.5px solid #e0dff8; border-radius: 10px; padding: 11px 14px; font-size: 14px; color: #26215c; outline: none; transition: border-color 0.15s; }
.pp-input:focus { border-color: #534ab7; background: #fff; }
.pp-input-disabled { background: #f0effc; color: #999; cursor: not-allowed; }

.pp-form-footer { display: flex; justify-content: space-between; align-items: center; padding-top: 0.5rem; }
.pp-save-hint { font-size: 12px; color: #aaa; }
.pp-btn-save { background: linear-gradient(135deg, #534ab7, #7f77dd); color: #fff; border: none; padding: 12px 28px; border-radius: 10px; font-size: 15px; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 8px; box-shadow: 0 4px 16px rgba(83,74,183,0.25); transition: opacity 0.15s, transform 0.15s; }
.pp-btn-save:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
.pp-btn-save:disabled { opacity: 0.5; cursor: not-allowed; }
.pp-spin { width: 15px; height: 15px; border: 2px solid rgba(255,255,255,0.4); border-top-color: #fff; border-radius: 50%; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Security info */
.pp-security-info { display: flex; align-items: flex-start; gap: 14px; background: #f4f3ff; border-radius: 12px; padding: 14px 16px; margin-bottom: 1.5rem; }
.pp-security-icon { font-size: 28px; flex-shrink: 0; }
.pp-security-info h3 { font-size: 15px; font-weight: 700; color: #26215c; margin-bottom: 4px; }
.pp-security-info p { font-size: 13px; color: #888; line-height: 1.5; }

@media (max-width: 600px) {
  .pp-hero { flex-direction: column; text-align: center; }
  .pp-form-row { grid-template-columns: 1fr; }
  .pp-form-footer { flex-direction: column; gap: 10px; align-items: stretch; }
  .pp-btn-save { justify-content: center; }
}
</style>
